// ========================================
// CONFIGURATION CLOUDINARY
// Portfolio Gustave KONATE
// ========================================

// Configuration Cloudinary
const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;
const CLOUDINARY_URL = `https://api.cloudinary.com/v1_1/${CLOUD_NAME}`;

/**
 * Upload un fichier vers Cloudinary
 * @param {File} file - Le fichier à uploader
 * @param {string} folder - Le dossier de destination (optionnel)
 * @param {function} onProgress - Callback pour la progression (optionnel)
 * @returns {Promise<object>} - Les données de l'image uploadée
 */
export const uploadToCloudinary = async (file, folder = 'portfolio', onProgress = null) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', UPLOAD_PRESET);
  formData.append('folder', folder);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    // Progression de l'upload
    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          onProgress(progress);
        }
      });
    }

    xhr.addEventListener('load', () => {
      if (xhr.status === 200) {
        const response = JSON.parse(xhr.responseText);
        resolve({
          url: response.secure_url,
          publicId: response.public_id,
          width: response.width,
          height: response.height,
          format: response.format,
          size: response.bytes,
          originalFilename: response.original_filename
        });
      } else {
        reject(new Error('Erreur lors de l\'upload'));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Erreur réseau lors de l\'upload'));
    });

    xhr.open('POST', `${CLOUDINARY_URL}/image/upload`);
    xhr.send(formData);
  });
};

/**
 * Upload une vidéo vers Cloudinary
 * @param {File} file - Le fichier vidéo à uploader
 * @param {string} folder - Le dossier de destination
 * @param {function} onProgress - Callback pour la progression
 * @returns {Promise<object>} - Les données de la vidéo uploadée
 */
export const uploadVideoToCloudinary = async (file, folder = 'portfolio/videos', onProgress = null) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', UPLOAD_PRESET);
  formData.append('folder', folder);
  formData.append('resource_type', 'video');

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          onProgress(progress);
        }
      });
    }

    xhr.addEventListener('load', () => {
      if (xhr.status === 200) {
        const response = JSON.parse(xhr.responseText);
        resolve({
          url: response.secure_url,
          publicId: response.public_id,
          duration: response.duration,
          format: response.format,
          size: response.bytes
        });
      } else {
        reject(new Error('Erreur lors de l\'upload de la vidéo'));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Erreur réseau lors de l\'upload'));
    });

    xhr.open('POST', `${CLOUDINARY_URL}/video/upload`);
    xhr.send(formData);
  });
};

/**
 * Upload un document (PDF, etc.) vers Cloudinary
 * @param {File} file - Le fichier document à uploader
 * @param {string} folder - Le dossier de destination
 * @param {function} onProgress - Callback pour la progression
 * @returns {Promise<object>} - Les données du document uploadé
 */
export const uploadDocumentToCloudinary = async (file, folder = 'portfolio/documents', onProgress = null) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', UPLOAD_PRESET);
  formData.append('folder', folder);
  formData.append('resource_type', 'raw');

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          onProgress(progress);
        }
      });
    }

    xhr.addEventListener('load', () => {
      if (xhr.status === 200) {
        const response = JSON.parse(xhr.responseText);
        resolve({
          url: response.secure_url,
          publicId: response.public_id,
          format: response.format,
          size: response.bytes,
          originalFilename: response.original_filename
        });
      } else {
        reject(new Error('Erreur lors de l\'upload du document'));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Erreur réseau lors de l\'upload'));
    });

    xhr.open('POST', `${CLOUDINARY_URL}/image/upload`);
    xhr.send(formData);
  });
};

/**
 * Générer une URL optimisée pour une image Cloudinary
 * @param {string} publicId - L'ID public de l'image
 * @param {object} options - Options de transformation
 * @returns {string} - URL optimisée
 */
export const getOptimizedImageUrl = (publicId, options = {}) => {
  const {
    width = 'auto',
    height = 'auto',
    crop = 'fill',
    quality = 'auto',
    format = 'auto'
  } = options;

  const transformations = [];
  
  if (width !== 'auto') transformations.push(`w_${width}`);
  if (height !== 'auto') transformations.push(`h_${height}`);
  if (crop) transformations.push(`c_${crop}`);
  transformations.push(`q_${quality}`);
  transformations.push(`f_${format}`);

  const transformString = transformations.join(',');
  
  return `https://res.cloudinary.com/${CLOUD_NAME}/image/upload/${transformString}/${publicId}`;
};

/**
 * Générer une URL de thumbnail
 * @param {string} publicId - L'ID public de l'image
 * @param {number} size - Taille du thumbnail (carré)
 * @returns {string} - URL du thumbnail
 */
export const getThumbnailUrl = (publicId, size = 200) => {
  return getOptimizedImageUrl(publicId, {
    width: size,
    height: size,
    crop: 'fill',
    quality: 'auto',
    format: 'auto'
  });
};

export { CLOUD_NAME, UPLOAD_PRESET, CLOUDINARY_URL };